import { RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { listen } from "./listen";

/** How long after older posts are added late layout changes (images loading) are corrected for. */
const HOLD_MS = 2000;

/** Posts mark themselves with their id so the timeline can find the one being read. */
const POST_SELECTOR = "[data-post-id]";

type ReadingAnchor = { postId: string; offset: number };

const postNodes = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(POST_SELECTOR));

/** The topmost post on screen, and how far below the top of the container it sits. */
const findAnchor = (container: HTMLElement): ReadingAnchor | null => {
  const top = container.getBoundingClientRect().top;
  const node = postNodes(container).find(
    (candidate) => candidate.getBoundingClientRect().bottom > top
  );
  if (!node?.dataset.postId) return null;

  return {
    postId: node.dataset.postId,
    offset: node.getBoundingClientRect().top - top,
  };
};

/**
 * Scrolls by however far the anchor post has moved. Measured rather than assumed, so it is right
 * whether or not the browser kept the position itself.
 */
const restoreAnchor = (container: HTMLElement, anchor: ReadingAnchor) => {
  const node = postNodes(container).find(
    (candidate) => candidate.dataset.postId === anchor.postId
  );
  if (!node) return;

  const offset =
    node.getBoundingClientRect().top - container.getBoundingClientRect().top;
  const drift = offset - anchor.offset;
  if (Math.abs(drift) >= 0.5) container.scrollTop += drift;
};

/**
 * Keeps the post the reader is looking at in place when older posts are added above it.
 * `isPrepending` marks the render that adds them.
 */
export const useReadingAnchor = (
  containerRef: RefObject<HTMLElement>,
  contentRef: RefObject<HTMLElement>,
  isPrepending: boolean
) => {
  const pending = useRef<ReadingAnchor | null>(null);
  const held = useRef<{ anchor: ReadingAnchor; until: number } | null>(null);

  // Read during render on purpose: by the commit the DOM already shows the new posts.
  if (isPrepending && containerRef.current) {
    pending.current = findAnchor(containerRef.current);
  }

  useLayoutEffect(() => {
    const container = containerRef.current;
    const anchor = pending.current;
    pending.current = null;
    if (!container || !anchor) return;

    restoreAnchor(container, anchor);
    held.current = { anchor, until: performance.now() + HOLD_MS };
  });

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (!held.current) return;
      if (performance.now() > held.current.until) {
        held.current = null;
        return;
      }
      restoreAnchor(container, held.current.anchor);
    });
    observer.observe(content);

    // Once the reader moves the list, where it sits is up to them.
    const stopListening = listen(
      container,
      ["touchstart", "wheel", "pointerdown", "keydown"],
      () => {
        held.current = null;
      }
    );

    return () => {
      observer.disconnect();
      stopListening();
    };
  }, [containerRef, contentRef]);
};
